<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  styles: {
    type: Array,
    required: true
  },
  modelValue: {
    type: [Number, String, null],
    default: null
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const selectStyle = (id) => {
  emit('update:modelValue', id)

  // Clear error when user makes a selection
  if (localErrors.value.selected_style) {
    delete localErrors.value.selected_style
  }
}

const isSelected = (id) => props.modelValue === id

const validate = () => {
  const errors = {}

  if (!props.modelValue) {
    errors.selected_style = 'Please select your teaching style'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="bg-white rounded-2xl shadow-lg p-10">
    <div class="mb-8">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Teaching Style <span class="text-red-500">*</span></h2>
      <p class="text-gray-600">How do you prefer to teach?</p>
      <p v-if="displayErrors.selected_style" class="text-red-500 text-sm mt-2 flex items-center">
        <span class="material-symbols-outlined text-sm mr-1">error</span>
        {{ displayErrors.selected_style }}
      </p>
    </div>

    <div class="grid grid-cols-3 gap-4">
      <button
        v-for="style in styles"
        :key="style.id"
        type="button"
        @click="selectStyle(style.id)"
        class="cursor-pointer group relative p-6 border-2 rounded-xl transition-all hover:shadow-md text-left"
        :class="isSelected(style.id) ? 'shadow-md' : 'border-gray-200 bg-white'"
        :style="{
          borderColor: isSelected(style.id) ? style.color : '',
          backgroundColor: isSelected(style.id) ? `${style.color}10` : ''
        }"
      >
        <div class="flex flex-col justify-start items-start mb-2">
          <div
            class="w-12 h-12 rounded-lg flex items-center justify-center"
            :style="{
              backgroundColor: isSelected(style.id) ? 'white' : `${style.color}10`,
              color: style.color
            }"
          >
            <span class="material-symbols-outlined text-xl">
              {{ style.icon_name || 'school' }}
            </span>
          </div>
        </div>
        <h3 class="text-md font-semibold text-gray-900 mb-1">{{ style.name }}</h3>
        <p class="text-sm text-gray-500">{{ style.description }}</p>
      </button>
    </div>
  </div>
</template>
